import axios, { AxiosInstance, AxiosResponse } from "axios";
import { toast } from "react-hot-toast";
import { configureMoney } from "../utils/money";

/**
 * The only shape a message attachment reference may take. Anchored at both ends so
 * an attacker-controlled string cannot smuggle in an absolute URL or a path escape.
 */
export const MESSAGE_ATTACHMENT_URL =
  /^\/api\/v1\/messages\/attachments\/([0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12})$/;

export interface ApiResponse<T = any> {
  success: boolean;
  data?: T;
  message?: string;
  error?: string;
  errors?: Record<string, string[]>;
}

export interface PaginatedResponse<T> extends ApiResponse<T[]> {
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
}

export interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  phone?: string;
  profileImage?: string;
  userType: "customer" | "provider" | "admin";
  isVerified: boolean;
  settings?: {
    notifications: {
      email: boolean;
      sms: boolean;
      push: boolean;
    };
    privacy: {
      showProfile: boolean;
      showLocation: boolean;
    };
  };
  createdAt: string;
}

export interface AuthResponse {
  user: User;
  accessToken: string;
  refreshToken: string;
}

export interface AiConfigurationEntry {
  value: string;
  source: "admin" | "environment";
  models: Array<{ provider: string; model: string }>;
}

export type AiConfiguration = Record<
  "fast" | "reasoning" | "synthesis" | "rebook" | "embedding" | "transcription" | "speech",
  AiConfigurationEntry
>;

export interface AppConfig {
  maxProvidersPerQuote: number;
  /** ISO code every price in this deployment is denominated in. */
  platformCurrency?: string;
  /** BCP-47 locale used to format money amounts. */
  platformLocale?: string;
  /** Hides the readiness entry point when the backend feature is off, so the
   *  button is never shown for an endpoint that would 404. */
  bookingReadinessEnabled?: boolean;
  ai?: AiConfiguration;
}

/** Defaults must fail closed for flags: unknown means off. */
export const DEFAULT_APP_CONFIG: AppConfig = {
  maxProvidersPerQuote: 5,
  bookingReadinessEnabled: false,
  platformCurrency: "BRL",
  platformLocale: "pt-BR",
};

export interface ReadinessEvidence {
  source: "booking" | "quote" | "request" | "provider" | "availability" | "message";
  recordId: string;
  field: string;
  excerpt?: string;
}

export interface ReadinessFinding {
  id: string;
  category:
    | "scope"
    | "access"
    | "materials"
    | "schedule"
    | "safety"
    | "payment"
    | "communication";
  severity: "info" | "attention" | "blocking";
  visibility: "shared" | "customer_only" | "provider_only";
  statement: string;
  evidence: ReadinessEvidence[];
  resolutionQuestion?: string;
}

export interface ReadinessPlan {
  bookingId: string;
  sourceFingerprint: string;
  readiness: "ready" | "needs_attention" | "blocked" | "incomplete";
  agreedScope: string[];
  exclusions: string[];
  findings: ReadinessFinding[];
  verification: {
    droppedCount: number;
    /** Stripped per reader by the API — the reasons quote rejected findings,
     *  which may be private to the other role. Only the count is exposed. */
    dropReasons?: string[];
    semanticReviewRan: boolean;
  };
  generatedAt: string;
  unavailableSections: string[];
  /**
   * Role-scoped preparation, narrowed server-side: the other participant's list
   * arrives empty, not hidden. Absent on plans generated before BR-2.
   */
  customerChecklist?: ReadinessChecklistItem[];
  providerChecklist?: ReadinessChecklistItem[];
}

export interface ReadinessMessageDraft {
  recipientRole: 'customer' | 'provider';
  resolvesFindingId: string;
  body: string;
  /** The language it was written in, inferred from the conversation. */
  language: string;
}

export interface ReadinessChecklistItem {
  id: string;
  category: string;
  label: string;
  evidence: Array<{ source: string; recordId: string; field: string; excerpt?: string }>;
}

/**
 * `unknown` means freshness could not be evaluated — never that the plan is
 * current. Treat it as unsafe to rely on.
 */
export type ReadinessFreshness = 'current' | 'stale' | 'unknown';

export interface ReadinessRunResponse {
  runId: string;
  status: string;
  plan: ReadinessPlan | null;
  freshness: ReadinessFreshness;
  /** Legacy two-state view of `freshness`: true for both stale and unknown. */
  stale: boolean;
  /**
   * True when the server served an existing plan instead of paying for a new
   * run, because the booking has not changed since that plan was built. Absent
   * on reads and on responses from a server predating the cost budget.
   */
  reused?: boolean;
}

export interface Provider {
  id: string;
  userId: string;
  businessName: string;
  description: string;
  services: string[];
  location:
    | {
        latitude: number;
        longitude: number;
        address: string;
        city: string;
        state: string;
        zipCode: string;
        country: string;
      }
    | string;
  serviceRadius: number;
  rating: number;
  totalReviews: number;
  portfolioImages: string[];
  profileImage?: string;
  profileCompletion?: number;
  isBackgroundChecked: boolean;
  isInsured: boolean;
  isActive: boolean;
  pricing: {
    baseRate: number;
    currency: string;
    rateType: "hourly" | "fixed" | "quote";
  };
  availableHours: {
    monday: { start: string; end: string; available: boolean };
    tuesday: { start: string; end: string; available: boolean };
    wednesday: { start: string; end: string; available: boolean };
    thursday: { start: string; end: string; available: boolean };
    friday: { start: string; end: string; available: boolean };
    saturday: { start: string; end: string; available: boolean };
    sunday: { start: string; end: string; available: boolean };
  };
  completedJobs: number;
  responseRate: number;
  averageResponseTime: number;
}

export interface Booking {
  id: string;
  customerId: string;
  providerId: string;
  // Origin link to the quote/request this booking was created from (null for
  // legacy/direct bookings). Used by the unified hub to dedupe the lifecycle card.
  quoteId?: string | null;
  requestId?: string | null;
  provider: Provider;
  customer: User;
  serviceType: string;
  description: string;
  location: {
    latitude: number;
    longitude: number;
    address: string;
    city: string;
    state: string;
    zipCode: string;
  };
  scheduledDate: string;
  estimatedDuration: number;
  status:
    | "pending"
    | "confirmed"
    | "in_progress"
    | "pending_completion"
    | "completed"
    | "in_dispute"
    | "cancelled";
  totalAmount: number;
  paymentStatus: "pending" | "paid" | "failed" | "refunded";
  specialInstructions?: string;
  reviews?: Array<{ id: string }>;
  createdAt: string;
  updatedAt: string;
}

export interface QuoteRequest {
  id: string;
  customerId: string;
  serviceType: string;
  description: string;
  location: {
    latitude: number;
    longitude: number;
    address: string;
    city: string;
    state: string;
    zipCode: string;
  };
  preferredDate?: string;
  budget?: {
    min: number;
    max: number;
    currency: string;
  };
  images?: string[];
  urgency: "low" | "medium" | "high" | "urgent";
  status: "open" | "closed" | "cancelled";
  requirements?: Array<{
    category: string;
    requirement: string;
    mandatory: boolean;
  }>;
  availability?: Array<{
    date: string;
    timeSlots: Array<{
      start: string;
      end: string;
    }>;
  }>;
  searchRadius: number;
  expiresAt?: string;
  // Present + non-empty ⇒ a direct request targeted at specific provider(s);
  // empty/absent ⇒ broadcast to all matching providers.
  targetProviderIds?: string[] | null;
  sourceBookingId?: string | null;
  quotesReceived: number;
  closedAt?: string;
  closureReason?: string;
  createdAt: string;
  updatedAt: string;
}

export interface FavoriteProvider {
  id: string;
  providerId: string;
  createdAt: string;
  provider: Provider;
}

export interface RebookPrefill {
  eligible: boolean;
  reason:
    | "booking_not_completed"
    | "provider_inactive"
    | "service_no_longer_offered"
    | "not_booking_customer"
    | null;
  sourceBookingId: string;
  provider: Provider;
  draft: {
    serviceType: string;
    description: string;
    specialInstructions: string;
    location: Booking["location"];
    estimatedDurationHours: number;
    proposedBudget: number;
    currency: string;
    requirements: NonNullable<QuoteRequest["requirements"]>;
  };
  references: { previousTotal: number; currentBaseRate: number | null };
  copiedFields: string[];
}

export interface Quote {
  id: string;
  requestId: string;
  providerId: string;
  customerId: string;
  serviceType: string;
  description: string;
  estimatedPrice: number;
  estimatedDuration: number;
  validUntil: string;
  status: "pending" | "accepted" | "rejected" | "expired" | "withdrawn";
  breakdown?: {
    labor: number;
    materials: number;
    equipment: number;
    other: number;
    tax: number;
  };
  terms?: Array<{
    item: string;
    description: string;
  }>;
  notes?: string;
  attachments?: string[];
  acceptedAt?: string;
  rejectedAt?: string;
  rejectionReason?: string;
  createdAt: string;
  updatedAt: string;
  provider?: Provider;
  customer?: User;
  request?: QuoteRequest;
}

export interface ProviderAnalysis {
  providerId: string;
  matchScore: number;
  strengths: string[];
  concerns: string[];
  reviewSentiment: string;
  uniqueValue?: string;
  riskFactors?: string[];
}

export interface Recommendation {
  rank: number;
  provider: {
    providerId: string;
    businessName: string;
    rating: number;
    totalReviews: number;
    services: string[];
    pricing?: {
      baseRate?: number;
      hourlyRate?: number;
      currency: string;
      rateType?: string;
    } | null;
  };
  analysis: ProviderAnalysis;
  reasoning: string;
  tradeoffs?: string[];
  bestFor?: string;
}

export interface WorkflowProviderResult {
  providerId: string;
  providerName: string;
  businessName: string;
  services: string[];
  matchScore: number;
  rating: number;
  totalReviews: number;
  pricing: {
    baseRate: number;
    currency: string;
    rateType: string;
  } | null;
  location: {
    city: string;
    state: string;
  } | null;
  isBackgroundChecked: boolean;
  isInsured: boolean;
}

export interface VerificationCheckResult {
  passed: boolean;
  notes: string;
}

export interface VerificationReport {
  passed: boolean;
  qualityScore: number;
  requirementsCoverage: VerificationCheckResult;
  recommendationQuality: VerificationCheckResult;
  completeness: VerificationCheckResult;
  suggestions: string[];
  timedOut?: boolean;
}

export interface WorkflowData {
  id: string;
  userId: string;
  status:
    | "pending"
    | "active"
    | "completed"
    | "failed"
    | "cancelled"
    | "waiting_for_user";
  currentAgent: string | null;
  agentHistory: Array<{
    agentName: string;
    action: string;
    status: string;
    startTime: string;
    endTime?: string;
    durationMs?: number;
    output?: Record<string, unknown>;
  }>;
  context: {
    workflowId: string;
    userId: string;
    userRequest: string;
    createdAt: string;
    requirements?: {
      isComplete: boolean;
      followUpQuestion?: string;
      extractedFacts?: string[];
      missingInformation?: string[];
      requirementsSummary?: {
        serviceType: string;
        location: {
          neighborhood?: string;
          address?: string;
          city?: string;
          state?: string;
          zipCode?: string;
        };
        timing: {
          preferredDate?: string;
          preferredTime?: string;
          isFlexible: boolean;
        };
        budget?: { min?: number; max?: number; hasFlexibility: boolean };
        specialRequirements: string[];
        urgency: "low" | "medium" | "high" | "emergency";
      };
    };
    searchResults?: WorkflowProviderResult[];
    analysisResults?: ProviderAnalysis[];
    recommendations?: Recommendation[];
    verification?: VerificationReport;
  };
  error?: {
    message: string;
    code?: string;
    agentName?: string;
  };
  createdAt: string;
  updatedAt: string;
  completedAt?: string;
}

// VITE_API_URL is intentionally unset when frontend is served by the same Express server —
// relative /api/v1 URLs resolve correctly in that case.

class ApiService {
  private api: AxiosInstance;
  private baseURL = import.meta.env.VITE_API_URL || "/api/v1";
  // Single-flight token refresh: concurrent 401s share one refresh call
  // instead of each firing its own (which previously caused a request storm).
  private refreshPromise: Promise<string> | null = null;

  constructor() {
    this.api = axios.create({
      baseURL: this.baseURL,
      timeout: 10000,
      headers: {
        "Content-Type": "application/json",
      },
    });

    this.setupInterceptors();
  }

  private setupInterceptors(): void {
    this.api.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem("accessToken");
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        // Tell the backend which locale to return server-generated messages in.
        const lng = (localStorage.getItem("i18nextLng") || "pt").toLowerCase();
        config.headers["X-Locale"] = lng.startsWith("en") ? "en" : "pt";
        return config;
      },
      (error) => {
        return Promise.reject(error);
      },
    );

    this.api.interceptors.response.use(
      (response: AxiosResponse) => {
        return response;
      },
      async (error) => {
        const originalRequest = error.config;
        const url: string = originalRequest?.url || "";
        // A 401 from the auth endpoints themselves must NOT trigger a refresh:
        // refreshing the refresh call recurses infinitely (the storm we saw).
        // A dead refresh token means the session is over — log out once.
        const isAuthEndpoint =
          url.includes("/auth/refresh") ||
          url.includes("/auth/logout") ||
          url.includes("/auth/login") ||
          url.includes("/auth/register");

        if (
          error.response?.status === 401 &&
          !originalRequest._retry &&
          !isAuthEndpoint
        ) {
          originalRequest._retry = true;
          try {
            const accessToken = await this.refreshAccessToken();
            originalRequest.headers.Authorization = `Bearer ${accessToken}`;
            return this.api(originalRequest);
          } catch (refreshError) {
            this.logout();
            window.location.href = "/login";
            return Promise.reject(refreshError);
          }
        }

        // Don't surface a toast for a failed refresh/logout — the redirect to
        // /login already communicates the expired session. (login/register
        // errors, e.g. "invalid credentials", still toast.)
        if (!url.includes("/auth/refresh") && !url.includes("/auth/logout")) {
          if (error.response?.data?.error) {
            toast.error(error.response.data.error);
          } else if (error.message) {
            toast.error(error.message);
          }
        }

        return Promise.reject(error);
      },
    );
  }

  // Auth methods
  async register(userData: {
    email: string;
    password: string;
    firstName: string;
    lastName: string;
    phone?: string;
    userType: "customer" | "provider";
  }): Promise<{ email: string; firstName: string }> {
    const response = await this.api.post<
      ApiResponse<{ email: string; firstName: string }>
    >("/auth/register", userData);
    return response.data.data!;
  }

  async verifyEmail(token: string): Promise<void> {
    await this.api.get("/auth/verify-email", { params: { token } });
  }

  async resendVerification(email: string): Promise<void> {
    await this.api.post("/auth/resend-verification", { email });
  }

  async forgotPassword(email: string): Promise<void> {
    await this.api.post("/auth/forgot-password", { email });
  }

  async resetPassword(token: string, password: string): Promise<void> {
    await this.api.post("/auth/reset-password", { token, password });
  }

  async login(email: string, password: string): Promise<AuthResponse> {
    const response = await this.api.post<ApiResponse<AuthResponse>>(
      "/auth/login",
      {
        email,
        password,
      },
    );
    const { accessToken, refreshToken } = response.data.data!;
    localStorage.setItem("accessToken", accessToken);
    localStorage.setItem("refreshToken", refreshToken);
    return response.data.data!;
  }

  // Refresh the access token, coalescing concurrent callers onto one in-flight
  // request. Throws if there's no refresh token or the refresh itself fails.
  private refreshAccessToken(): Promise<string> {
    if (!this.refreshPromise) {
      const refreshToken = localStorage.getItem("refreshToken");
      if (!refreshToken) {
        return Promise.reject(new Error("No refresh token"));
      }
      this.refreshPromise = this.api
        .post("/auth/refresh", { refreshToken })
        .then((response) => {
          const { accessToken, refreshToken: newRefreshToken } =
            response.data.data;
          localStorage.setItem("accessToken", accessToken);
          localStorage.setItem("refreshToken", newRefreshToken);
          return accessToken as string;
        })
        .finally(() => {
          this.refreshPromise = null;
        });
    }
    return this.refreshPromise;
  }

  async logout(): Promise<void> {
    try {
      await this.api.post("/auth/logout");
    } catch (error) {
      // Continue with logout even if API call fails
    } finally {
      localStorage.removeItem("accessToken");
      localStorage.removeItem("refreshToken");
      localStorage.removeItem("user");
    }
  }

  async getProfile(): Promise<User> {
    const response = await this.api.get<ApiResponse<User>>("/auth/profile");
    return response.data.data!;
  }

  /**
   * Portable copy of everything the platform holds about the signed-in user.
   *
   * Assembled server-side. The client cannot build this itself — it would only
   * ever contain what the UI happens to have endpoints for, which is how the
   * export came to be a copy of the profile screen. Requested as a blob and
   * saved as-is so the file the user opens is exactly what the server produced.
   */
  async exportMyData(): Promise<Blob> {
    const response = await this.api.get("/users/export", {
      responseType: "blob",
      // Larger than the default: this walks every collection the account touches.
      timeout: 120000,
    });
    return response.data as Blob;
  }

  async updateProfile(updates: Partial<User>): Promise<User> {
    const response = await this.api.put<ApiResponse<User>>(
      "/auth/profile",
      updates,
    );
    return response.data.data!;
  }

  async changePassword(
    currentPassword: string,
    newPassword: string,
  ): Promise<void> {
    await this.api.put("/auth/password", {
      currentPassword,
      newPassword,
    });
  }

  async deleteAccount(): Promise<void> {
    await this.api.delete("/users/profile");
  }

  // Provider methods
  async getProviders(params?: {
    page?: number;
    limit?: number;
    latitude?: number;
    longitude?: number;
    radius?: number;
    serviceType?: string;
  }): Promise<PaginatedResponse<Provider>> {
    const response = await this.api.get<PaginatedResponse<Provider>>(
      "/providers",
      {
        params,
      },
    );
    return response.data;
  }

  async getProvider(id: string): Promise<Provider> {
    const response = await this.api.get<ApiResponse<Provider>>(
      `/providers/${id}`,
    );
    return response.data.data!;
  }

  // Booking methods
  async getBookings(params?: {
    page?: number;
    limit?: number;
    status?: string;
  }): Promise<PaginatedResponse<Booking>> {
    const response = await this.api.get<PaginatedResponse<Booking>>(
      "/bookings",
      {
        params,
      },
    );
    return response.data;
  }

  async createBooking(bookingData: {
    providerId: string;
    serviceType: string;
    description: string;
    location: {
      latitude: number;
      longitude: number;
      address: string;
      city: string;
      state: string;
      zipCode: string;
    };
    scheduledDate: string;
    estimatedDuration: number;
    totalAmount: number;
    specialInstructions?: string;
  }): Promise<Booking> {
    const response = await this.api.post<ApiResponse<Booking>>(
      "/bookings",
      bookingData,
    );
    return response.data.data!;
  }

  async updateBooking(id: string, updates: Partial<Booking>): Promise<Booking> {
    const response = await this.api.put<ApiResponse<Booking>>(
      `/bookings/${id}`,
      updates,
    );
    return response.data.data!;
  }

  async getBooking(id: string): Promise<Booking> {
    const response = await this.api.get<ApiResponse<Booking>>(
      `/bookings/${id}`,
    );
    return response.data.data!;
  }

  async cancelBooking(id: string, reason?: string): Promise<void> {
    await this.api.delete(`/bookings/${id}`, { data: { reason } });
  }

  // Location and GPS methods
  async geocodeAddress(address: string): Promise<any> {
    const response = await this.api.post<ApiResponse<any>>(
      "/locations/geocode",
      {
        address,
      },
    );
    return response.data.data;
  }

  // Address typeahead — returns [] gracefully if Places API is unavailable (the
  // caller then falls back to free-text + geocode validation).
  async addressAutocomplete(
    input: string,
  ): Promise<Array<{ description: string; placeId: string }>> {
    try {
      const response = await this.api.get<
        ApiResponse<Array<{ description: string; placeId: string }>>
      >(`/locations/autocomplete?input=${encodeURIComponent(input)}`);
      return response.data.data ?? [];
    } catch {
      return [];
    }
  }

  async resolvePlace(
    placeId: string,
  ): Promise<{
    address: string;
    city: string;
    state: string;
    zipCode: string;
    latitude: number;
    longitude: number;
  }> {
    const response = await this.api.get<ApiResponse<any>>(
      `/locations/resolve-place/${placeId}`,
    );
    return response.data.data;
  }

  async reverseGeocode(latitude: number, longitude: number): Promise<any> {
    const response = await this.api.post<ApiResponse<any>>(
      "/locations/reverse-geocode",
      {
        latitude,
        longitude,
      },
    );
    return response.data.data;
  }

  async searchProvidersGPS(params: {
    latitude: number;
    longitude: number;
    radius?: number;
    serviceTypes?: string[];
    sortBy?: "distance" | "rating" | "price" | "response_time";
    minRating?: number;
    maxPrice?: number;
    hasInsurance?: boolean;
    hasBackgroundCheck?: boolean;
    isAvailable?: boolean;
    page?: number;
    limit?: number;
  }): Promise<
    ApiResponse<{
      providers: (Provider & {
        distance: number;
        distanceText: string;
        duration: number;
        durationText: string;
      })[];
      totalCount: number;
      page: number;
      totalPages: number;
      searchParams: any;
    }>
  > {
    // TEMPORARY WORKAROUND: Use GET endpoint with query params instead of POST
    const queryParams = new URLSearchParams();
    queryParams.append("latitude", params.latitude.toString());
    queryParams.append("longitude", params.longitude.toString());
    if (params.radius) queryParams.append("radius", params.radius.toString());

    // Send every selected service, not just the first. This used to append
    // `serviceTypes[0]` and discard the rest, so picking three services silently
    // searched for one of them. A provider matches if they offer any of them.
    if (params.serviceTypes && params.serviceTypes.length > 0) {
      for (const serviceType of params.serviceTypes) {
        queryParams.append("serviceTypes", serviceType);
      }
    }

    if (params.sortBy) queryParams.append("sortBy", params.sortBy);
    if (params.minRating && params.minRating > 0)
      queryParams.append("minRating", params.minRating.toString());
    if (params.maxPrice)
      queryParams.append("maxRate", params.maxPrice.toString());

    // Only send boolean filters when they are TRUE (enabled)
    if (params.hasInsurance === true) queryParams.append("isInsured", "true");
    if (params.hasBackgroundCheck === true)
      queryParams.append("isBackgroundChecked", "true");
    if (params.isAvailable === true) queryParams.append("isVerified", "true");

    if (params.page) queryParams.append("page", params.page.toString());
    if (params.limit) queryParams.append("limit", params.limit.toString());

    const response = await this.api.get<
      ApiResponse<{
        providers: (Provider & {
          distance: number;
          distanceText: string;
          duration: number;
          durationText: string;
        })[];
        totalCount: number;
        page: number;
        totalPages: number;
        searchParams: any;
      }>
    >(`/locations/providers/search?${queryParams.toString()}`);
    return response.data;
  }

  async calculateDistance(
    origin: { latitude: number; longitude: number },
    destination: { latitude: number; longitude: number },
  ): Promise<{ distance: number; duration: number; route?: any }> {
    const response = await this.api.post<ApiResponse<any>>(
      "/locations/distance",
      {
        origin,
        destination,
      },
    );
    return response.data.data;
  }

  async searchNearbyPlaces(
    latitude: number,
    longitude: number,
    radius: number,
    type?: string,
  ): Promise<any[]> {
    const response = await this.api.post<ApiResponse<any[]>>(
      "/locations/nearby-search",
      {
        latitude,
        longitude,
        radius,
        type,
      },
    );
    return response.data.data || [];
  }

  // Quote Request methods
  async getFavoriteProviders(): Promise<FavoriteProvider[]> {
    const response = await this.api.get<
      ApiResponse<{ favorites: FavoriteProvider[] }>
    >("/providers/favorites");
    return response.data.data?.favorites || [];
  }

  async addFavoriteProvider(providerId: string): Promise<void> {
    await this.api.post(`/providers/${providerId}/favorite`);
  }

  async removeFavoriteProvider(providerId: string): Promise<void> {
    await this.api.delete(`/providers/${providerId}/favorite`);
  }

  async getRebookPrefill(bookingId: string): Promise<RebookPrefill> {
    const response = await this.api.get<ApiResponse<RebookPrefill>>(
      `/bookings/${bookingId}/rebook-prefill`,
    );
    return response.data.data!;
  }

  async createRebookRequest(
    bookingId: string,
    draft: RebookPrefill["draft"] & { preferredDate: string },
  ): Promise<QuoteRequest> {
    const response = await this.api.post<
      ApiResponse<{ quoteRequest: QuoteRequest }>
    >(`/bookings/${bookingId}/rebook-request`, draft);
    return response.data.data!.quoteRequest;
  }

  async refineRebookDraft(
    bookingId: string,
    draft: Pick<
      RebookPrefill["draft"],
      | "serviceType"
      | "description"
      | "specialInstructions"
      | "estimatedDurationHours"
      | "proposedBudget"
    >,
    changeRequest: string,
  ): Promise<{
    draft: typeof draft;
    changedFields: string[];
    summary: string;
  }> {
    const response = await this.api.post<
      ApiResponse<{
        draft: typeof draft;
        changedFields: string[];
        summary: string;
      }>
    >(`/bookings/${bookingId}/rebook-refine`, { draft, changeRequest });
    return response.data.data!;
  }

  async createQuoteRequest(requestData: {
    serviceType: string;
    description: string;
    location: {
      latitude?: number;
      longitude?: number;
      address?: string;
      city: string;
      state?: string;
      zipCode?: string;
    };
    preferredDate?: string;
    budget?: {
      min: number;
      max: number;
      currency: string;
    };
    images?: string[];
    urgency: "low" | "medium" | "high" | "urgent";
    requirements?: Array<{
      category: string;
      requirement: string;
      mandatory: boolean;
    }>;
    availability?: Array<{
      date: string;
      timeSlots: Array<{
        start: string;
        end: string;
      }>;
    }>;
    searchRadius?: number;
    expiresAt?: string;
    targetProviderIds?: string[];
  }): Promise<QuoteRequest> {
    const response = await this.api.post<
      ApiResponse<{ quoteRequest: QuoteRequest }>
    >("/quotes/requests", requestData);
    return response.data.data!.quoteRequest;
  }

  async getQuoteRequest(requestId: string): Promise<QuoteRequest> {
    const response = await this.api.get<
      ApiResponse<{ quoteRequest: QuoteRequest }>
    >(`/quotes/requests/${requestId}`);
    return response.data.data!.quoteRequest;
  }

  async updateQuoteRequest(
    requestId: string,
    updates: Partial<QuoteRequest>,
  ): Promise<QuoteRequest> {
    const response = await this.api.put<
      ApiResponse<{ quoteRequest: QuoteRequest }>
    >(`/quotes/requests/${requestId}`, updates);
    return response.data.data!.quoteRequest;
  }

  async closeQuoteRequest(
    requestId: string,
    reason?: string,
  ): Promise<QuoteRequest> {
    const response = await this.api.post<
      ApiResponse<{ quoteRequest: QuoteRequest }>
    >(`/quotes/requests/${requestId}/close`, { reason });
    return response.data.data!.quoteRequest;
  }

  async searchQuoteRequests(params?: {
    page?: number;
    limit?: number;
    serviceType?: string;
    status?: string;
    urgency?: string;
    minBudget?: number;
    maxBudget?: number;
    latitude?: number;
    longitude?: number;
    radius?: number;
  }): Promise<PaginatedResponse<QuoteRequest>> {
    const response = await this.api.get<
      ApiResponse<{
        quoteRequests: QuoteRequest[];
        total: number;
        page: number;
        limit: number;
      }>
    >("/quotes/requests", { params });
    const d = response.data.data!;
    return {
      success: true,
      data: d.quoteRequests,
      pagination: {
        page: d.page,
        limit: d.limit,
        total: d.total,
        pages: Math.ceil(d.total / d.limit),
      },
    };
  }

  // Quote methods
  async createQuote(quoteData: {
    requestId: string;
    serviceType: string;
    description: string;
    estimatedPrice: number;
    estimatedDuration: number;
    validUntil: string;
    breakdown?: {
      labor: number;
      materials: number;
      equipment: number;
      other: number;
      tax: number;
    };
    terms?: Array<{
      item: string;
      description: string;
    }>;
    notes?: string;
    attachments?: string[];
  }): Promise<Quote> {
    const response = await this.api.post<ApiResponse<{ quote: Quote }>>(
      "/quotes",
      quoteData,
    );
    return response.data.data!.quote;
  }

  async getQuote(quoteId: string): Promise<Quote> {
    const response = await this.api.get<ApiResponse<{ quote: Quote }>>(
      `/quotes/${quoteId}`,
    );
    return response.data.data!.quote;
  }

  async updateQuote(quoteId: string, updates: Partial<Quote>): Promise<Quote> {
    const response = await this.api.put<ApiResponse<{ quote: Quote }>>(
      `/quotes/${quoteId}`,
      updates,
    );
    return response.data.data!.quote;
  }

  async updateQuoteStatus(
    quoteId: string,
    status: "accepted" | "rejected" | "withdrawn",
    reason?: string,
  ): Promise<Quote> {
    const response = await this.api.put<ApiResponse<{ quote: Quote }>>(
      `/quotes/${quoteId}/status`,
      { status, reason },
    );
    return response.data.data!.quote;
  }

  async withdrawQuote(quoteId: string): Promise<Quote> {
    const response = await this.api.delete<ApiResponse<{ quote: Quote }>>(
      `/quotes/${quoteId}`,
    );
    return response.data.data!.quote;
  }

  async getQuotesForRequest(
    requestId: string,
    params?: {
      page?: number;
      limit?: number;
      sortBy?: "price" | "rating" | "createdAt";
      sortOrder?: "asc" | "desc";
    },
  ): Promise<PaginatedResponse<Quote>> {
    const response = await this.api.get<PaginatedResponse<Quote>>(
      `/quotes/requests/${requestId}/quotes`,
      {
        params,
      },
    );
    return response.data;
  }

  async searchQuotes(params?: {
    page?: number;
    limit?: number;
    status?: string;
    minPrice?: number;
    maxPrice?: number;
    serviceType?: string;
  }): Promise<PaginatedResponse<Quote>> {
    const response = await this.api.get<
      ApiResponse<{
        quotes: Quote[];
        total: number;
        page: number;
        limit: number;
      }>
    >("/quotes", { params });
    const d = response.data.data!;
    return {
      success: true,
      data: d.quotes,
      pagination: {
        page: d.page,
        limit: d.limit,
        total: d.total,
        pages: Math.ceil(d.total / d.limit),
      },
    };
  }

  // Messaging methods
  async getConversations(params?: {
    type?: "direct" | "group" | "support";
    isActive?: boolean;
    page?: number;
    limit?: number;
    sortBy?: "lastMessage" | "created" | "title";
    sortOrder?: "asc" | "desc";
  }): Promise<PaginatedResponse<any>> {
    const response = await this.api.get<PaginatedResponse<any>>(
      "/messages/conversations",
      {
        params,
      },
    );
    return response.data;
  }

  async createConversation(conversationData: {
    participantIds: string[];
    title?: string;
    description?: string;
    type?: "direct" | "group" | "support";
    metadata?: {
      bookingId?: string;
      quoteRequestId?: string;
      serviceType?: string;
    };
  }): Promise<any> {
    const response = await this.api.post<ApiResponse<any>>(
      "/messages/conversations",
      conversationData,
    );
    return response.data.data!;
  }

  async uploadMessageAttachment(
    file: File,
  ): Promise<{
    url: string;
    originalName: string;
    mimeType: string;
    size: number;
  }> {
    const formData = new FormData();
    formData.append("file", file);
    const response = await this.api.post<
      ApiResponse<{
        url: string;
        originalName: string;
        mimeType: string;
        size: number;
      }>
    >(
      "/messages/messages/upload",
      formData,
      // File transfers (up to 10MB) can far exceed the 10s default on slow
      // connections — give them a generous timeout so they don't abort mid-upload.
      { headers: { "Content-Type": "multipart/form-data" }, timeout: 60000 },
    );
    return response.data.data!;
  }

  /**
   * Fetch a private message attachment as a blob.
   *
   * Attachments are no longer static files, so they cannot be pointed at directly
   * from an `<img src>` or `<a href>` — those requests carry no Authorization
   * header. The caller turns the blob into an object URL and is responsible for
   * revoking it.
   */
  async fetchMessageAttachment(
    url: string,
  ): Promise<{ blob: Blob; fileName: string | null }> {
    // A message's `attachments` entries are arbitrary strings chosen by the sender.
    // Handing one straight to the authenticated axios client would let a malicious
    // sender store an absolute external URL and have the *viewer's* browser send
    // their bearer token to that origin. Only a canonical attachment id is accepted,
    // and the request path is rebuilt from it rather than taken from the message.
    const id = MESSAGE_ATTACHMENT_URL.exec(url)?.[1];
    if (!id) {
      throw new Error("Unrecognized attachment reference");
    }

    const response = await this.api.get(`/messages/attachments/${id}`, {
      responseType: "blob",
      timeout: 60000,
    });

    // A message stores only the attachment URL, so the display name and type come
    // back with the file itself: the filename from Content-Disposition, the type
    // from the blob. Deriving either from the URL does not work — these paths
    // carry no extension.
    const disposition = response.headers["content-disposition"] as
      | string
      | undefined;

    // Prefer the RFC 5987 `filename*`, which carries the real UTF-8 name; the plain
    // `filename` is an ASCII-only fallback with non-Latin-1 characters replaced.
    const extended = disposition?.match(/filename\*=UTF-8''([^;]+)/i);
    const plain = disposition?.match(/filename="([^"]*)"/);
    let fileName: string | null = null;
    if (extended?.[1]) {
      try {
        fileName = decodeURIComponent(extended[1]);
      } catch {
        fileName = null;
      }
    }
    if (!fileName) fileName = plain?.[1] ?? null;

    return { blob: response.data as Blob, fileName };
  }

  /**
   * People the current user may start a conversation with — those they share a
   * booking or quote with. Deliberately not a directory of all users.
   */
  async getConversationContacts(search?: string): Promise<Array<{
    id: string;
    firstName: string;
    lastName: string;
    userType: "customer" | "provider" | "admin";
    profileImage?: string | null;
  }>> {
    const query = search ? `?search=${encodeURIComponent(search)}` : "";
    const response = await this.api.get<ApiResponse<any[]>>(
      `/messages/contacts${query}`,
    );
    return response.data.data ?? [];
  }

  async getConversation(conversationId: string): Promise<any> {
    const response = await this.api.get<ApiResponse<any>>(
      `/messages/conversations/${conversationId}`,
    );
    return response.data.data!;
  }

  async sendMessage(messageData: {
    conversationId: string;
    message: string;
    messageType?: "text" | "image" | "file";
    attachments?: string[];
    replyToMessageId?: string;
  }): Promise<any> {
    const response = await this.api.post<ApiResponse<any>>(
      "/messages/messages",
      messageData,
    );
    return response.data.data!;
  }

  async getMessages(
    conversationId: string,
    params?: {
      senderId?: string;
      receiverId?: string;
      messageType?: "text" | "image" | "file";
      isRead?: boolean;
      page?: number;
      limit?: number;
      sortOrder?: "asc" | "desc";
    },
  ): Promise<PaginatedResponse<any>> {
    const response = await this.api.get<PaginatedResponse<any>>(
      `/messages/conversations/${conversationId}/messages`,
      {
        params,
      },
    );
    return response.data;
  }

  async markMessagesAsRead(conversationId: string): Promise<void> {
    await this.api.patch(
      `/messages/conversations/${conversationId}/messages/read`,
    );
  }

  async updateMessage(
    messageId: string,
    updates: {
      message?: string;
      attachments?: string[];
    },
  ): Promise<any> {
    const response = await this.api.patch<ApiResponse<any>>(
      `/messages/messages/${messageId}`,
      updates,
    );
    return response.data.data!;
  }

  async deleteMessage(messageId: string): Promise<void> {
    await this.api.delete(`/messages/messages/${messageId}`);
  }

  async getUnreadMessageCount(): Promise<{ count: number }> {
    const response = await this.api.get<ApiResponse<{ count: number }>>(
      "/messages/messages/unread/count",
    );
    return response.data.data!;
  }

  // Payment methods
  async getPayments(params?: {
    status?: string;
    type?: string;
    page?: number;
    limit?: number;
    sortBy?: string;
    sortOrder?: "asc" | "desc";
  }): Promise<PaginatedResponse<any>> {
    const response = await this.api.get<PaginatedResponse<any>>("/payments", {
      params,
    });
    return response.data;
  }

  async getPayment(paymentId: string): Promise<any> {
    const response = await this.api.get<ApiResponse<any>>(
      `/payments/${paymentId}`,
    );
    return response.data.data!;
  }

  // Amount and currency are derived from the booking server-side; the client does
  // not get to choose what it is charged.
  async refundPayment(
    paymentId: string,
    refundData: {
      amount?: number;
      reason?: string;
      refundApplicationFee?: boolean;
      reverseTransfer?: boolean;
    },
  ): Promise<any> {
    const response = await this.api.post<ApiResponse<any>>(
      `/payments/${paymentId}/refund`,
      refundData,
    );
    return response.data.data!;
  }

  async getCustomerPayments(
    customerId: string,
    params?: {
      page?: number;
      limit?: number;
      status?: string;
    },
  ): Promise<PaginatedResponse<any>> {
    const response = await this.api.get<PaginatedResponse<any>>(
      `/payments/customer/${customerId}`,
      {
        params,
      },
    );
    return response.data;
  }

  async getProviderPayments(
    providerId: string,
    params?: {
      page?: number;
      limit?: number;
      status?: string;
    },
  ): Promise<PaginatedResponse<any>> {
    const response = await this.api.get<PaginatedResponse<any>>(
      `/payments/provider/${providerId}`,
      {
        params,
      },
    );
    return response.data;
  }

  // AI Assistant Workflow methods
  async startWorkflow(
    initialMessage: string,
  ): Promise<{ workflowId: string; status: string }> {
    const response = await this.api.post<
      ApiResponse<{ workflowId: string; status: string }>
    >(
      "/agentic-assistant/workflows",
      { initialMessage },
      { timeout: 30000 }, // LLM calls can take longer
    );
    return response.data.data!;
  }

  async getWorkflow(workflowId: string): Promise<WorkflowData> {
    const response = await this.api.get<
      ApiResponse<{ workflow: WorkflowData }>
    >(`/agentic-assistant/workflows/${workflowId}`);
    return response.data.data!.workflow;
  }

  async sendWorkflowMessage(
    workflowId: string,
    message: string,
  ): Promise<WorkflowData> {
    const response = await this.api.post<
      ApiResponse<{ workflow: WorkflowData }>
    >(
      `/agentic-assistant/workflows/${workflowId}/messages`,
      { message },
      { timeout: 30000 },
    );
    return response.data.data!.workflow;
  }

  async cancelWorkflow(workflowId: string): Promise<void> {
    await this.api.delete(`/agentic-assistant/workflows/${workflowId}`);
  }

  // Health check
  async healthCheck(): Promise<any> {
    const response = await this.api.get("/health");
    return response.data;
  }

  // Review methods
  async getReviews(params?: {
    providerId?: string;
    customerId?: string;
    bookingId?: string;
    rating?: number;
    page?: number;
    limit?: number;
    sortBy?: string;
    sortOrder?: "asc" | "desc";
  }): Promise<PaginatedResponse<any>> {
    const response = await this.api.get<PaginatedResponse<any>>("/reviews", {
      params,
    });
    return response.data;
  }

  async getReview(reviewId: string): Promise<any> {
    const response = await this.api.get<ApiResponse<any>>(
      `/reviews/${reviewId}`,
    );
    return response.data.data!;
  }

  async createReview(reviewData: {
    bookingId: string;
    providerId: string;
    rating: number;
    title: string;
    comment: string;
    criteria?: {
      quality: number;
      timeliness: number;
      communication: number;
      professionalism: number;
      value: number;
    };
    photos?: string[];
    isAnonymous?: boolean;
  }): Promise<any> {
    const response = await this.api.post<ApiResponse<any>>(
      "/reviews",
      reviewData,
    );
    return response.data.data!;
  }

  async updateReview(
    reviewId: string,
    reviewData: {
      rating?: number;
      title?: string;
      comment?: string;
      criteria?: {
        quality?: number;
        timeliness?: number;
        communication?: number;
        professionalism?: number;
        value?: number;
      };
      photos?: string[];
      isAnonymous?: boolean;
    },
  ): Promise<any> {
    const response = await this.api.put<ApiResponse<any>>(
      `/reviews/${reviewId}`,
      reviewData,
    );
    return response.data.data!;
  }

  async deleteReview(reviewId: string): Promise<void> {
    await this.api.delete(`/reviews/${reviewId}`);
  }

  async addProviderResponse(reviewId: string, response: string): Promise<any> {
    const responseData = await this.api.post<ApiResponse<any>>(
      `/reviews/${reviewId}/response`,
      { response },
    );
    return responseData.data.data!;
  }

  async draftProviderResponse(
    reviewId: string,
  ): Promise<{ draftResponse: string }> {
    const responseData = await this.api.post<ApiResponse<any>>(
      `/reviews/${reviewId}/draft-response`,
      {},
    );
    return responseData.data.data!;
  }

  async getProviderReviews(
    providerId: string,
    params?: {
      page?: number;
      limit?: number;
      rating?: number;
      sortBy?: string;
      sortOrder?: "asc" | "desc";
    },
  ): Promise<PaginatedResponse<any>> {
    const response = await this.api.get<PaginatedResponse<any>>(
      `/reviews/provider/${providerId}`,
      {
        params,
      },
    );
    return response.data;
  }

  async getMyCustomerReviews(params?: {
    page?: number;
    limit?: number;
    sortBy?: string;
    sortOrder?: "asc" | "desc";
  }): Promise<PaginatedResponse<any>> {
    const response = await this.api.get<PaginatedResponse<any>>(
      "/reviews/customer/my",
      {
        params,
      },
    );
    return response.data;
  }

  async getMyProviderReviews(params?: {
    page?: number;
    limit?: number;
    rating?: number;
    sortBy?: string;
    sortOrder?: "asc" | "desc";
  }): Promise<PaginatedResponse<any>> {
    const response = await this.api.get<PaginatedResponse<any>>(
      "/reviews/provider/my",
      {
        params,
      },
    );
    return response.data;
  }

  async flagReview(
    reviewId: string,
    reason: string,
    details?: string,
  ): Promise<any> {
    const response = await this.api.post<ApiResponse<any>>(
      `/reviews/${reviewId}/flag`,
      {
        reason,
        details,
      },
    );
    return response.data.data!;
  }

  async getReviewAnalytics(providerId: string): Promise<any> {
    const response = await this.api.get<ApiResponse<any>>(
      `/reviews/analytics/${providerId}`,
    );
    return response.data.data!;
  }

  async searchReviews(params?: {
    query?: string;
    providerId?: string;
    rating?: number;
    dateFrom?: string;
    dateTo?: string;
    page?: number;
    limit?: number;
  }): Promise<PaginatedResponse<any>> {
    const response = await this.api.get<PaginatedResponse<any>>(
      "/reviews/search",
      {
        params,
      },
    );
    return response.data;
  }

  // Provider Dashboard methods
  async getProviderDashboardStats(period: string = "month"): Promise<any> {
    const response = await this.api.get(
      `/providers/my/dashboard-stats?period=${period}`,
    );
    return response.data;
  }

  async getProviderBookings(
    params: {
      status?: string;
      page?: number;
      limit?: number;
    } = {},
  ): Promise<PaginatedResponse<any>> {
    const searchParams = new URLSearchParams();
    if (params.status) searchParams.append("status", params.status);
    if (params.page) searchParams.append("page", params.page.toString());
    if (params.limit) searchParams.append("limit", params.limit.toString());

    const response = await this.api.get(
      `/bookings/provider/my?${searchParams}`,
    );
    return response.data;
  }

  async getProviderProfile(providerId: string): Promise<Provider> {
    const response = await this.api.get(`/providers/${providerId}`);
    return response.data;
  }

  async getMyProviderProfile(): Promise<ApiResponse<{ provider: Provider }>> {
    const response = await this.api.get(`/providers/my`);
    return response.data;
  }

  async updateProviderProfile(
    providerId: string,
    data: Partial<Provider>,
  ): Promise<Provider> {
    const response = await this.api.put(`/providers/${providerId}`, data);
    return response.data;
  }

  async updateAvailability(
    availability: Provider["availableHours"],
  ): Promise<Provider["availableHours"]> {
    const response = await this.api.patch<
      ApiResponse<Provider["availableHours"]>
    >("/providers/availability", availability);
    return response.data.data!;
  }

  async getServiceCatalog(): Promise<string[]> {
    const response = await this.api.get<ApiResponse<{ services: string[] }>>(
      "/providers/services/catalog",
    );
    return response.data.data?.services ?? [];
  }

  async updateBookingStatus(bookingId: string, status: string): Promise<any> {
    const response = await this.api.put(`/bookings/${bookingId}/status`, {
      status,
    });
    return response.data;
  }

  async startBooking(bookingId: string): Promise<any> {
    const response = await this.api.post(`/bookings/${bookingId}/start`, {});
    return response.data;
  }

  async markBookingComplete(bookingId: string): Promise<any> {
    const response = await this.api.post(`/bookings/${bookingId}/complete`, {});
    return response.data;
  }

  async confirmBookingCompletion(bookingId: string): Promise<any> {
    const response = await this.api.post(
      `/bookings/${bookingId}/confirm-completion`,
      {},
    );
    return response.data;
  }

  async disputeBooking(bookingId: string, reason: string): Promise<any> {
    const response = await this.api.post(`/bookings/${bookingId}/dispute`, {
      reason,
    });
    return response.data;
  }

  // Notification methods
  async getUserNotifications(
    params: {
      type?: string;
      page?: number;
      limit?: number;
      unreadOnly?: boolean;
    } = {},
  ): Promise<PaginatedResponse<any>> {
    const searchParams = new URLSearchParams();
    if (params.type) searchParams.append("type", params.type);
    if (params.page) searchParams.append("page", params.page.toString());
    if (params.limit) searchParams.append("limit", params.limit.toString());
    if (params.unreadOnly) searchParams.append("unreadOnly", "true");

    const response = await this.api.get(`/notifications?${searchParams}`);
    return response.data;
  }

  async getUnreadNotificationCount(): Promise<{ unreadCount: number }> {
    const response = await this.api.get("/notifications/unread/count");
    return response.data?.data ?? response.data;
  }

  async markNotificationsRead(notificationIds: string[]): Promise<any> {
    const response = await this.api.patch("/notifications/read", {
      notificationIds,
    });
    return response.data;
  }

  async markAllNotificationsRead(): Promise<any> {
    const response = await this.api.patch("/notifications/read-all");
    return response.data;
  }

  async deleteNotifications(notificationIds: string[]): Promise<any> {
    const response = await this.api.delete("/notifications", {
      data: { notificationIds },
    });
    return response.data;
  }

  async getNotificationPreferences(): Promise<any> {
    const response = await this.api.get("/notifications/preferences");
    return response.data?.data ?? response.data;
  }

  async updateNotificationPreferences(preferences: any): Promise<any> {
    const response = await this.api.put(
      "/notifications/preferences",
      preferences,
    );
    return response.data;
  }

  // ── Admin methods ──────────────────────────────────────────────────

  async getAdminDashboard(): Promise<any> {
    const response = await this.api.get<ApiResponse<any>>("/admin/dashboard");
    return response.data.data!;
  }

  async getAdminUsers(params?: {
    page?: number;
    limit?: number;
    userType?: string;
    isActive?: boolean;
    search?: string;
  }): Promise<any> {
    const response = await this.api.get<ApiResponse<any>>("/admin/users", {
      params,
    });
    return response.data.data!;
  }

  async updateUserStatus(
    userId: string,
    payload: {
      isActive: boolean;
      suspensionReason?: string;
      suspensionComment?: string;
      suspendedUntil?: string | null;
    },
  ): Promise<any> {
    const response = await this.api.put<ApiResponse<any>>(
      `/admin/users/${userId}/status`,
      payload,
    );
    return response.data.data!;
  }

  async getPendingProviders(params?: {
    page?: number;
    limit?: number;
  }): Promise<any> {
    const response = await this.api.get<ApiResponse<any>>(
      "/admin/providers/pending",
      { params },
    );
    return response.data.data!;
  }

  async verifyProvider(
    providerId: string,
    payload: {
      approved: boolean;
      notes?: string;
      isBackgroundChecked?: boolean;
      isInsured?: boolean;
    },
  ): Promise<any> {
    const response = await this.api.post<ApiResponse<any>>(
      `/admin/providers/${providerId}/verify`,
      payload,
    );
    return response.data.data!;
  }

  async getFlaggedReviews(params?: {
    page?: number;
    limit?: number;
  }): Promise<any> {
    const response = await this.api.get<ApiResponse<any>>(
      "/admin/reviews/flagged",
      { params },
    );
    return response.data.data!;
  }

  async moderateReview(
    reviewId: string,
    payload: { action: "approve" | "delete" | "keep_flagged"; reason?: string },
  ): Promise<any> {
    const response = await this.api.put<ApiResponse<any>>(
      `/admin/reviews/${reviewId}/moderate`,
      payload,
    );
    return response.data.data!;
  }

  // Utility methods
  isAuthenticated(): boolean {
    return !!localStorage.getItem("accessToken");
  }

  getStoredUser(): User | null {
    const user = localStorage.getItem("user");
    return user ? JSON.parse(user) : null;
  }

  setStoredUser(user: User): void {
    localStorage.setItem("user", JSON.stringify(user));
  }

  clearStoredUser(): void {
    localStorage.removeItem("accessToken");
    localStorage.removeItem("refreshToken");
    localStorage.removeItem("user");
  }

  // Generic HTTP methods for ad-hoc requests (admin pages, payment forms, etc.)
  get(path: string, config?: any) {
    return this.api.get(path, config);
  }
  post(path: string, data?: any, config?: any) {
    return this.api.post(path, data, config);
  }
  put(path: string, data?: any, config?: any) {
    return this.api.put(path, data, config);
  }
  patch(path: string, data?: any, config?: any) {
    return this.api.patch(path, data, config);
  }
  delete(path: string, config?: any) {
    return this.api.delete(path, config);
  }

  async voiceTranscribe(audioBlob: Blob, filename: string): Promise<string> {
    const form = new FormData();
    form.append("audio", audioBlob, filename);
    // Use fetch directly — axios instance default 'Content-Type: application/json' breaks
    // multipart uploads by overwriting the browser-generated boundary parameter
    const token = localStorage.getItem("accessToken");
    const res = await fetch(`${this.baseURL}/voice/transcribe`, {
      method: "POST",
      headers: { Authorization: `Bearer ${token}` },
      body: form,
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({ error: res.statusText }));
      throw new Error(err.error ?? res.statusText);
    }
    if (res.status === 204) return ""; // voice not configured server-side → no transcript
    const json = await res.json();
    return json.data?.transcript ?? "";
  }

  async voiceSynthesize(text: string): Promise<Blob> {
    const token = localStorage.getItem("accessToken");
    const res = await fetch(`${this.baseURL}/voice/synthesize`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ text }),
    });
    if (!res.ok) throw new Error(res.statusText);
    if (res.status === 204) return new Blob([]); // voice not configured → empty (caller skips)
    return res.blob();
  }

  // Public, read-only client config (admin-tunable app_settings). Falls back to
  // sane defaults if the request fails so the UI never blocks on it.
  async getAppConfig(): Promise<AppConfig> {
    try {
      const res =
        await this.api.get<ApiResponse<AppConfig>>(
          "/config",
        );
      const config = res.data.data ?? DEFAULT_APP_CONFIG;
      // Seed the money formatter from the same values the server charges in, so
      // display and charge can never disagree.
      configureMoney(config.platformCurrency, config.platformLocale);
      return config;
    } catch {
      return DEFAULT_APP_CONFIG;
    }
  }

  async getAdminAiConfiguration(): Promise<AiConfiguration> {
    const res = await this.api.get<ApiResponse<AiConfiguration>>("/admin/ai-configuration");
    return res.data.data!;
  }

  async updateAdminAiConfiguration(field: string, value: string): Promise<AiConfiguration> {
    const res = await this.api.put<ApiResponse<AiConfiguration>>(
      `/admin/ai-configuration/${field}`,
      { value },
    );
    return res.data.data!;
  }

  // ── Booking Readiness Copilot ───────────────────────────────────────────
  // Read-only: these endpoints return an advisory plan. Acting on a finding
  // still goes through the existing messaging/booking APIs.

  async createReadinessRun(bookingId: string): Promise<ReadinessRunResponse> {
    const res = await this.api.post<ApiResponse<ReadinessRunResponse>>(
      `/bookings/${bookingId}/readiness-runs`,
      undefined,
      // The run is synchronous and calls two models: ~25s typical, and the
      // backend's own stage timeouts allow up to 135s. The 10s client default
      // aborts mid-run — the server still finishes, but the response is lost and
      // the UI silently keeps showing the previous plan. (BR-2 replaces this
      // with SSE progress, at which point the long POST goes away.)
      { timeout: 150000 },
    );
    return res.data.data!;
  }

  /**
   * Writes one message the user may send about one finding.
   *
   * Returns text only: it creates no conversation, stores no message and sends
   * nothing. Sending stays the user's act, through the messaging API.
   */
  async draftReadinessMessage(
    runId: string,
    findingId: string,
  ): Promise<ReadinessMessageDraft> {
    const res = await this.api.post<ApiResponse<ReadinessMessageDraft>>(
      `/readiness-runs/${runId}/findings/${findingId}/draft`,
      undefined,
      // One small model call, but still a model call.
      { timeout: 45000 },
    );
    return res.data.data!;
  }

  async getLatestReadinessRun(bookingId: string): Promise<ReadinessRunResponse | null> {
    const res = await this.api.get<ApiResponse<ReadinessRunResponse | null>>(
      `/bookings/${bookingId}/readiness-runs/latest`,
    );
    return res.data.data ?? null;
  }
}

export const apiService = new ApiService();
export default apiService;
